import moment from "moment";
import React, {
	createContext,
	Dispatch,
	SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import client from "utils/client";
import { sendErrorToWebhook } from "utils/sendWebhook";
import { usePropertyContext } from "./context";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import {
	ExemptionCurrentlyReceivingValues,
	ExemptionPIIRefType,
	ExemptionStatus,
	FileUploadProps,
	OneZeroOrNull,
	PropertyDetails,
	PropertyDetailsExemptionFields,
} from "utils/types";

import { ExemptionQuestions } from "utils/types/exemption-questions";

import {
	valueIsQuestionId,
	getQuestionComponent,
} from "./Exemptions/getQuestionComponent";

import { californiaExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/california.machine";
import { floridaExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/florida.machine";
import { georgiaExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/georgia.machine";
import { illinoisExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/illinois.machine";
import { michiganExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/michigan.machine";
import { newYorkExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/newYork.machine";
import { pennsylvaniaExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/pennsylvania.machine";
import { texasExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/texas.machine";
import { researchExemptionQuestionFlowMachine } from "./Exemptions/questionFlowMachines/research.machine";

import { useMachine } from "@xstate/react";
import { FileUploadType } from "elements/Input/FileUpload";
import { usePropertyValueContext } from "./PropertyValueChart";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { useGenericFlowEventTracker } from "context/TrackEventsContext";
import { useAuthContext } from "context/AuthContext";
import ButtonV2 from "elements/ButtonV2";

const MYSQL_DATE_FORMAT = "YYYY-MM-DD";
const WEBSITE_DATE_FORMAT = "L";

export const EXEMPTION_DL_NARS = [
	"Wrong Drivers License",
	"DL Address Mismatch",
	"Drivers License Expired",
	"Missing Drivers License",
];

export const EXEMPTION_FILE_RELATED_NARS = [
	"Additional Docs Required",
	"Cannot Verify Address/Ownership",
	"Marriage Certificate",
	"Utility Bills (Dependent on tax years applied)",
	"Divorce Decree",
	"Trust Documents",
	"Previous Address",
	"UDI Form",
	"Vehicle Registration",
];

export const EXEMPTION_NY_TAX_NARS = ["New York - Tax Info Needed"];
export const EXEMPTION_NY_INVALID_CREDENTIALS_NARS = [
	"New York - Invalid Credentials",
];

type TrackEventData = {
	trackingEventData?: any;
	skipStep?: boolean;
};

const useRestartExemptionApplication = () => {
	const router = useRouter();
	const pathname = usePathname();
	const [restartingApplication, setRestartingApplication] =
		React.useState(false);
	const [exemptionRestarted, setRestartExemption] = React.useState(false); // Needed to bypass "Not Qualified" or "Already has Homestead" statuses, allowing users to restart their exemption if they believe the info is incorrect or they made a mistake

	const { details, setModalToShow } = usePropertyContext();

	const trackEvent = useEventTracker();

	const { userData } = useAuthContext();

	const restartApplication = async () => {
		if (details?.exemption) {
			try {
				const propertyId = details?.id;
				if (!propertyId) return;
				setRestartingApplication(true);
				await client.restartExemptionApplication({
					propertyId,
				});
				// Hard refresh the page to reset the contexts
				window.location.href = `${pathname}/exemptions`;
			} catch (e) {
				sendErrorToWebhook(
					`Error restarting exemption application for property id ${details?.id}`
				);
			} finally {
				setRestartingApplication(false);
			}
		} else {
			setRestartExemption(true);
			setModalToShow("exemptions");
			trackEvent({
				eventName: constants.EVENTS.W2S_Exemption_Start_CTA,
				data: {
					CustomerId: userData?.id,
					PropertyId: details?.id,
					Address: details?.address,
				},
			});
		}
	};

	/* eslint react/display-name: "off" */
	const RestartExemptionApplicationButton: typeof ButtonV2 =
		React.useMemo(() => {
			return ({ children, ...props }) => (
				<ButtonV2
					size="mobile-extra-small"
					variant="primary-outline"
					{...props}
					disabled={restartingApplication}
					onClick={restartApplication}>
					{children ?? "Restart Application"}
				</ButtonV2>
			);
		}, [restartingApplication, restartApplication]);

	return {
		setRestartExemption,
		restartApplication,
		restartingApplication,
		RestartExemptionApplicationButton,
		exemptionRestarted,
	};
};

interface ExemptionsContextType
	extends ExemptionQuestions.FlowContext,
		ReturnType<typeof useRestartExemptionApplication> {
	flowId: string | undefined;
	showBackButton: boolean;
	activeQuestionId: string;
	showDriversLicenseNARs: boolean;
	showFileRelatedNARs: boolean;
	showNYTaxNars: boolean;
	showNYCredentialsNars: boolean;
	isEligibleForRetro: boolean | undefined;
	next: (args?: TrackEventData) => void;
	prev: () => void;
	Question:
		| React.FC<{ ExitButton?: React.FC; SkipButton?: React.FC }>
		| undefined;
	status: ExemptionStatus | undefined;
	archiveReason: string | null | undefined;
	setFutureSavingsAmount: (value: number | undefined) => void;
	setRetroSavingsAmount: (value: number | undefined) => void;
	setPrimaryResidenceValue: (value: boolean | undefined) => void;
	setCurrentlyReceivingValue: (
		value: ExemptionCurrentlyReceivingValues | undefined
	) => void;
	setTransferringHomesteadValue: (value: boolean | undefined) => void;
	setPurchaseDateValue: (value: string | undefined) => void;
	setMoveInDateValue: (value: string | undefined) => void;
	setOwnerOneValue: (value: number | undefined) => void;
	setOwnerTwoValue: (value: number | undefined) => void;
	setDobValue: (value: string | undefined) => void;
	setPreviousAddressValue: (value: string | undefined) => void;
	setPreviousAddress2Value: (value: string | undefined) => void;
	setPreviousCityValue: (value: string | undefined) => void;
	setPreviousStateValue: (value: string | undefined) => void;
	setPreviousZipcodeValue: (value: string | undefined) => void;
	setPreviousCountyValue: (value: string | undefined) => void;
	setPreviousMoveOutDateValue: (value: string | undefined) => void;
	setAdditionalOwnerFirstNameValue: (value: string | undefined) => void;
	setAdditionalOwnerLastNameValue: (value: string | undefined) => void;
	setAdditionalOwnerEmailValue: (value: string | undefined) => void;
	setAdditionalOwnerPhoneValue: (value: string | undefined) => void;
	setAdditionalOwnerDobValue: (value: string | undefined) => void;
	setOtherCountiesValue: (value: string | undefined) => void;
	setMaritalStatusValue: (value: string | undefined) => void;
	setOwnersValue: (value: string | undefined) => void;
	setCurrentSolarWindExemptionsValue: (value: boolean | undefined) => void;
	setInstallingSolarValue: (value: boolean | undefined) => void;
	setResidencyProofFilesToUpload: (
		fn: (value: FileUploadType[]) => FileUploadType[]
	) => void;
	setTrustDocumentFilesToUpload: (value: FileUploadType[]) => void;
	setOwnerDlFilesToUpload: (value: FileUploadType[]) => void;
	setOwnerDlVerificationErrored: (value: boolean | undefined) => void;
	setOwnerHasUploadedDlFile: (value: boolean | undefined) => void;
	setOwnerDlExemptValue: (value: boolean | undefined) => void;
	setOwner2DlFilesToUpload: (value: FileUploadType[]) => void;
	setOwner2DlVerificationErrored: (value: boolean | undefined) => void;
	setOwner2HasUploadedDlFile: (value: boolean | undefined) => void;
	setOwner2DlExemptValue: (value: boolean | undefined) => void;
	setOwnerSsnTin: (value: string | undefined) => void;
	setOwnerHasEnteredSsnTin: (value: boolean | undefined) => void;
	setOwner2SsnTin: (value: string | undefined) => void;
	setOwner2HasEnteredSsnTin: (value: boolean | undefined) => void;
	setSolarWindInstalled: (value: boolean | undefined) => void;
	setOtherHeirOwnersOccupy: (value: boolean | undefined) => void;
	setIsInheritedProperty: (value: boolean | undefined) => void;
	setIsVeteran: (value: boolean | undefined) => void;
	setHasDisability: (value: boolean | undefined) => void;
	setSurvivingSpouseOf65PlusPerson: (value: boolean | undefined) => void;
	setDisabledVeteranOrSurvivingSpouse: (value: boolean | undefined) => void;
	setSurvivingSpouseOfVeteranKilledInLineOfDuty: (
		value: boolean | undefined
	) => void;
	setSurvivingSpouseOfFirstResponderKilledInLineOfDuty: (
		value: boolean | undefined
	) => void;
	setDonatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse: (
		value: boolean | undefined
	) => void;
	setOwnedThroughCooperativeHousing: (value: boolean | undefined) => void;
	setOwnedThroughNonProfit: (value: boolean | undefined) => void;
	setCollectRentAtThisProperty: (value: boolean | undefined) => void;
	setNoneOfTheAbove: (value: boolean | undefined) => void;
	setSqFtIncomeProducing: (value: number | undefined) => void;
	setManufacturedHomeMake: (value: string | undefined) => void;
	setManufacturedHomeModel: (value: string | undefined) => void;
	setManufacturedHomeId: (value: string | undefined) => void;
	setTransferMoveOutDate: (value: string | undefined) => void;
	setTransferSaleDate: (value: string | undefined) => void;
	setTransferOwnerDidNotSellProperty: (value: boolean | undefined) => void;
	setOwner1DlNumberValue: (value: string | undefined) => void;
	setOwner2DlNumberValue: (value: string | undefined) => void;
	setOwner1HasEnteredDlNumber: (value: boolean | undefined) => void;
	setOwner2HasEnteredDlNumber: (value: boolean | undefined) => void;
	setSpecialFieldsValue: (
		value: undefined | Partial<PropertyDetailsExemptionFields["special_fields"]>
	) => void;
	exemptionId: number | undefined;
	setExemptionId: (value: number | undefined) => void;
	setNyGovIdAccountUsername: (value: string | undefined) => void;
	setNyGovIdAccountPassword: (value: string | undefined) => void;
}

export const ExemptionsContext = createContext<ExemptionsContextType>({
	restartApplication: async () => {},
	setRestartExemption: () => {},
	restartingApplication: false,
	RestartExemptionApplicationButton: () => null,
	exemptionRestarted: false,
	flowId: undefined,
	activeQuestionId: "",
	showBackButton: false,
	exemptionsProgress: 0,
	next: (args?: TrackEventData) => {},
	prev: () => {},
	showDriversLicenseNARs: false,
	showFileRelatedNARs: false,
	showNYTaxNars: false,
	showNYCredentialsNars: false,
	Question: undefined,
	primaryResidenceValue: undefined,
	currentMarketValue: null,
	use_info: null,
	state: "",
	county: "",
	city: "",
	auth_company_type: null,
	exemption_requires_both_licenses: 0,
	status: undefined,
	archiveReason: undefined,
	setPrimaryResidenceValue: () => {},
	currentlyReceivingValue: undefined,
	setCurrentlyReceivingValue: () => {},
	transferringHomesteadValue: undefined,
	setTransferringHomesteadValue: () => {},
	purchaseDateValue: undefined,
	setPurchaseDateValue: () => {},
	moveInDateValue: undefined,
	setMoveInDateValue: () => {},
	ownerOneValue: undefined,
	setOwnerOneValue: () => {},
	ownerTwoValue: undefined,
	setOwnerTwoValue: () => {},
	dobValue: undefined,
	setDobValue: () => {},
	previousAddressValue: undefined,
	setPreviousAddressValue: () => {},
	previousAddress2Value: undefined,
	setPreviousAddress2Value: () => {},
	previousCityValue: undefined,
	setPreviousCityValue: () => {},
	previousStateValue: undefined,
	setPreviousStateValue: () => {},
	previousZipcodeValue: undefined,
	setPreviousZipcodeValue: () => {},
	previousCountyValue: undefined,
	setPreviousCountyValue: () => {},
	previousMoveOutDateValue: undefined,
	setPreviousMoveOutDateValue: () => {},
	additionalOwnerFirstNameValue: undefined,
	setAdditionalOwnerFirstNameValue: () => {},
	additionalOwnerLastNameValue: undefined,
	setAdditionalOwnerLastNameValue: () => {},
	additionalOwnerEmailValue: undefined,
	setAdditionalOwnerEmailValue: () => {},
	additionalOwnerPhoneValue: undefined,
	setAdditionalOwnerPhoneValue: () => {},
	additionalOwnerDobValue: undefined,
	setAdditionalOwnerDobValue: () => {},
	otherCountiesValue: undefined,
	setOtherCountiesValue: () => {},
	maritalStatusValue: undefined,
	setMaritalStatusValue: () => {},
	ownersValue: undefined,
	setOwnersValue: () => {},
	currentSolarWindExemptionsValue: undefined,
	setCurrentSolarWindExemptionsValue: () => {},
	installingSolarValue: undefined,
	setInstallingSolarValue: () => {},
	owner1SsnTin: undefined,
	setOwnerSsnTin: () => {},
	owner1HasEnteredSsnTin: undefined,
	setOwnerHasEnteredSsnTin: () => {},
	owner2SsnTin: undefined,
	setOwner2SsnTin: () => {},
	owner2HasEnteredSsnTin: undefined,
	setOwner2HasEnteredSsnTin: () => {},
	residencyProofFilesToUpload: [],
	setResidencyProofFilesToUpload: () => {},
	trustDocumentFilesToUpload: [],
	setTrustDocumentFilesToUpload: () => {},
	ownerDlFilesToUpload: [],
	setOwnerDlFilesToUpload: () => {},
	ownerDlVerificationErrored: undefined,
	setOwnerDlVerificationErrored: () => {},
	ownerHasUploadedDlFile: undefined,
	setOwnerHasUploadedDlFile: () => {},
	ownerDlExemptValue: undefined,
	setOwnerDlExemptValue: () => {},
	owner2DlFilesToUpload: [],
	owner2DlVerificationErrored: undefined,
	setOwner2DlVerificationErrored: () => {},
	setOwner2DlFilesToUpload: () => {},
	owner2HasUploadedDlFile: undefined,
	setOwner2HasUploadedDlFile: () => {},
	owner2DlExemptValue: undefined,
	setOwner2DlExemptValue: () => {},
	solarWindInstalled: undefined,
	setSolarWindInstalled: () => {},
	otherHeirOwnersOccupy: undefined,
	setOtherHeirOwnersOccupy: () => {},
	isVeteran: undefined,
	setIsVeteran: () => {},
	isInheritedProperty: undefined,
	setIsInheritedProperty: () => {},
	hasDisability: undefined,
	setHasDisability: () => {},
	survivingSpouseOf65PlusPerson: undefined,
	setSurvivingSpouseOf65PlusPerson: () => {},
	disabledVeteranOrSurvivingSpouse: undefined,
	setDisabledVeteranOrSurvivingSpouse: () => {},
	survivingSpouseOfVeteranKilledInLineOfDuty: undefined,
	setSurvivingSpouseOfVeteranKilledInLineOfDuty: () => {},
	survivingSpouseOfFirstResponderKilledInLineOfDuty: undefined,
	setSurvivingSpouseOfFirstResponderKilledInLineOfDuty: () => {},
	donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse: undefined,
	setDonatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse: () => {},
	ownedThroughCooperativeHousing: undefined,
	setOwnedThroughCooperativeHousing: () => {},
	ownedThroughNonProfit: undefined,
	setOwnedThroughNonProfit: () => {},
	collectRentAtThisProperty: undefined,
	setCollectRentAtThisProperty: () => {},
	noneOfTheAbove: undefined,
	setNoneOfTheAbove: () => {},
	sqFtIncomeProducing: undefined,
	setSqFtIncomeProducing: () => {},
	isEligibleForRetro: false,
	futureSavingsAmount: undefined,
	setFutureSavingsAmount: () => {},
	retroSavingsAmount: undefined,
	setRetroSavingsAmount: () => {},
	manufacturedHomeMake: undefined,
	setManufacturedHomeMake: () => {},
	manufacturedHomeModel: undefined,
	setManufacturedHomeModel: () => {},
	manufacturedHomeId: undefined,
	setManufacturedHomeId: () => {},
	transferMoveOutDate: undefined,
	setTransferMoveOutDate: () => {},
	transferSaleDate: undefined,
	setTransferSaleDate: () => {},
	transferOwnerDidNotSellProperty: undefined,
	setTransferOwnerDidNotSellProperty: () => {},
	owner1DlNumberValue: undefined,
	setOwner1DlNumberValue: () => {},
	owner2DlNumberValue: undefined,
	setOwner2DlNumberValue: () => {},
	owner1HasEnteredDlNumber: undefined,
	setOwner1HasEnteredDlNumber: () => {},
	owner2HasEnteredDlNumber: undefined,
	setOwner2HasEnteredDlNumber: () => {},
	specialFieldsValue: undefined,
	setSpecialFieldsValue: () => {},
	exemptionId: undefined,
	setExemptionId: () => {},
	nyGovIdAccountUsername: undefined,
	setNyGovIdAccountUsername: () => {},
	nyGovIdAccountPassword: undefined,
	setNyGovIdAccountPassword: () => {},
}); // default values

export const useExemptionsContext = () => useContext(ExemptionsContext);

const useFlowMachine = ({
	currentMarketValue,
	state,
	county,
	accepting_exemptions,
}: {
	currentMarketValue: number | null;
	state: string;
	county: string;
	accepting_exemptions?: OneZeroOrNull;
}) => {
	const californiaFlowMachine = useMachine(
		californiaExemptionQuestionFlowMachine,
		{
			input: {
				currentMarketValue,
				ownerDlFilesToUpload: [],
				owner2DlFilesToUpload: [],
				residencyProofFilesToUpload: [],
				trustDocumentFilesToUpload: [],
			},
		}
	);
	const texasFlowMachine = useMachine(texasExemptionQuestionFlowMachine, {
		input: {
			currentMarketValue,
			ownerDlFilesToUpload: [],
			owner2DlFilesToUpload: [],
			residencyProofFilesToUpload: [],
			trustDocumentFilesToUpload: [],
		},
	});
	const georgiaFlowMachine = useMachine(georgiaExemptionQuestionFlowMachine, {
		input: {
			currentMarketValue,
			ownerDlFilesToUpload: [],
			owner2DlFilesToUpload: [],
			residencyProofFilesToUpload: [],
			trustDocumentFilesToUpload: [],
		},
	});
	const newYorkFlowMachine = useMachine(newYorkExemptionQuestionFlowMachine, {
		input: {
			currentMarketValue,
			ownerDlFilesToUpload: [],
			owner2DlFilesToUpload: [],
			residencyProofFilesToUpload: [],
			trustDocumentFilesToUpload: [],
		},
	});
	const illinoisFlowMachine = useMachine(illinoisExemptionQuestionFlowMachine, {
		input: {
			currentMarketValue,
			ownerDlFilesToUpload: [],
			owner2DlFilesToUpload: [],
			residencyProofFilesToUpload: [],
			trustDocumentFilesToUpload: [],
		},
	});
	const floridaFlowMachine = useMachine(floridaExemptionQuestionFlowMachine, {
		input: {
			currentMarketValue,
			ownerDlFilesToUpload: [],
			owner2DlFilesToUpload: [],
			residencyProofFilesToUpload: [],
			trustDocumentFilesToUpload: [],
		},
	});
	const michiganFlowMachine = useMachine(michiganExemptionQuestionFlowMachine, {
		input: {
			currentMarketValue,
			ownerDlFilesToUpload: [],
			owner2DlFilesToUpload: [],
			residencyProofFilesToUpload: [],
			trustDocumentFilesToUpload: [],
		},
	});
	const pennsylvaniaFlowMMachine = useMachine(
		pennsylvaniaExemptionQuestionFlowMachine,
		{
			input: {
				currentMarketValue,
				ownerDlFilesToUpload: [],
				owner2DlFilesToUpload: [],
				residencyProofFilesToUpload: [],
				trustDocumentFilesToUpload: [],
			},
		}
	);

	const researchFlowMachine = useMachine(researchExemptionQuestionFlowMachine, {
		input: {
			currentMarketValue,
			ownerDlFilesToUpload: [],
			owner2DlFilesToUpload: [],
			residencyProofFilesToUpload: [],
			trustDocumentFilesToUpload: [],
		},
	});

	if (!!accepting_exemptions) {
		if (state === "CA") return californiaFlowMachine;
		if (state === "GA" && ["DeKalb", "Fulton"].includes(county))
			return georgiaFlowMachine;
		if (state === "NY") return newYorkFlowMachine;
		if (state === "IL" && ["Cook"].includes(county)) return illinoisFlowMachine;
		if (state === "FL") return floridaFlowMachine;
		if (state === "MI") return michiganFlowMachine;
		if (state === "PA") return pennsylvaniaFlowMMachine;
		if (state === "TX") return texasFlowMachine;
	}
	return researchFlowMachine;
};

export const ExemptionsContextProvider: React.FC<{ id: string }> = ({
	children,
	id,
}) => {
	const searchParams = useSearchParams();
	const { details, loading } = usePropertyContext();
	const trackEvent = useEventTracker();
	const { modalToShow } = usePropertyContext();
	const { currentMarketValue } = usePropertyValueContext();
	const { trackAnswerEvent, trackViewPageEvent, trackBackEvent, initFlowName } =
		useGenericFlowEventTracker();
	//initFlowName("EXEMPTIONS_FLOW");
	const {
		RestartExemptionApplicationButton,
		exemptionRestarted,
		restartingApplication,
		restartApplication,
		setRestartExemption,
	} = useRestartExemptionApplication();
	const [exemptionId, setExemptionId] = useState<number | undefined>();

	useEffect(() => {
		if (modalToShow === "exemptions") {
			initFlowName("EXEMPTIONS_FLOW");
		}
	}, [modalToShow]);

	const status = useMemo<ExemptionStatus | undefined>(() => {
		if (!details) return;
		if (details?.is_homestead) return "Already Has Homestead";
		if (details?.exemption_status) return details.exemption_status;
		return "Not Started";
	}, [details]);

	const archiveReason = useMemo(() => {
		if (!details) return;
		return details?.exemption?.archive_reason;
	}, [details]);

	const [state, send, service] = useFlowMachine({
		currentMarketValue,
		state: details?.state || "",
		county: details?.county || "",
		accepting_exemptions: details?.accepting_exemptions,
	});

	const flowId = state.machine.id;

	const setPrimaryResidenceValue = (value: boolean | undefined) => {
		send({ type: "update_context", context: { primaryResidenceValue: value } });
	};

	const setCurrentlyReceivingValue = (
		value: ExemptionCurrentlyReceivingValues | undefined
	) => {
		send({
			type: "update_context",
			context: { currentlyReceivingValue: value },
		});
	};
	const setTransferringHomesteadValue = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { transferringHomesteadValue: value },
		});
	};
	const setPurchaseDateValue = (value: string | undefined) => {
		send({ type: "update_context", context: { purchaseDateValue: value } });
	};
	const setMoveInDateValue = (value: string | undefined) => {
		send({ type: "update_context", context: { moveInDateValue: value } });
	};
	const setOwnerOneValue = (value: number | undefined) => {
		send({ type: "update_context", context: { ownerOneValue: value } });
	};
	const setOwnerTwoValue = (value: number | undefined) => {
		send({ type: "update_context", context: { ownerTwoValue: value } });
	};
	const setDobValue = (value: string | undefined) => {
		send({ type: "update_context", context: { dobValue: value } });
	};
	const setPreviousAddressValue = (value: string | undefined) => {
		send({ type: "update_context", context: { previousAddressValue: value } });
	};
	const setPreviousAddress2Value = (value: string | undefined) => {
		send({ type: "update_context", context: { previousAddress2Value: value } });
	};
	const setPreviousCityValue = (value: string | undefined) => {
		send({ type: "update_context", context: { previousCityValue: value } });
	};
	const setPreviousStateValue = (value: string | undefined) => {
		send({ type: "update_context", context: { previousStateValue: value } });
	};
	const setPreviousZipcodeValue = (value: string | undefined) => {
		send({ type: "update_context", context: { previousZipcodeValue: value } });
	};
	const setPreviousCountyValue = (value: string | undefined) => {
		send({ type: "update_context", context: { previousCountyValue: value } });
	};
	const setPreviousMoveOutDateValue = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { previousMoveOutDateValue: value },
		});
	};
	const setAdditionalOwnerFirstNameValue = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { additionalOwnerFirstNameValue: value },
		});
	};
	const setAdditionalOwnerLastNameValue = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { additionalOwnerLastNameValue: value },
		});
	};
	const setAdditionalOwnerEmailValue = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { additionalOwnerEmailValue: value },
		});
	};
	const setAdditionalOwnerPhoneValue = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { additionalOwnerPhoneValue: value },
		});
	};
	const setAdditionalOwnerDobValue = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { additionalOwnerDobValue: value },
		});
	};
	const setOtherCountiesValue = (value: string | undefined) => {
		send({ type: "update_context", context: { otherCountiesValue: value } });
	};
	const setOwnersValue = (value: string | undefined) => {
		send({ type: "update_context", context: { ownersValue: value } });
	};
	const setMaritalStatusValue = (value: string | undefined) => {
		send({ type: "update_context", context: { maritalStatusValue: value } });
	};
	const setCurrentSolarWindExemptionsValue = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { currentSolarWindExemptionsValue: value },
		});
	};
	const setInstallingSolarValue = (value: boolean | undefined) => {
		send({ type: "update_context", context: { installingSolarValue: value } });
	};
	const setOwnerDlExemptValue = (value: boolean | undefined) => {
		send({ type: "update_context", context: { ownerDlExemptValue: value } });
	};
	const setOwner2DlExemptValue = (value: boolean | undefined) => {
		send({ type: "update_context", context: { owner2DlExemptValue: value } });
	};
	const setSolarWindInstalled = (value: boolean | undefined) => {
		send({ type: "update_context", context: { solarWindInstalled: value } });
	};
	const setOtherHeirOwnersOccupy = (value: boolean | undefined) => {
		send({ type: "update_context", context: { otherHeirOwnersOccupy: value } });
	};
	const setIsInheritedProperty = (value: boolean | undefined) => {
		send({ type: "update_context", context: { isInheritedProperty: value } });
	};
	const setIsVeteran = (value: boolean | undefined) => {
		send({ type: "update_context", context: { isVeteran: value } });
	};
	const setHasDisability = (value: boolean | undefined) => {
		send({ type: "update_context", context: { hasDisability: value } });
	};
	const setSurvivingSpouseOf65PlusPerson = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { survivingSpouseOf65PlusPerson: value },
		});
	};
	const setDisabledVeteranOrSurvivingSpouse = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { disabledVeteranOrSurvivingSpouse: value },
		});
	};
	const setSurvivingSpouseOfVeteranKilledInLineOfDuty = (
		value: boolean | undefined
	) => {
		send({
			type: "update_context",
			context: { survivingSpouseOfVeteranKilledInLineOfDuty: value },
		});
	};
	const setSurvivingSpouseOfFirstResponderKilledInLineOfDuty = (
		value: boolean | undefined
	) => {
		send({
			type: "update_context",
			context: { survivingSpouseOfFirstResponderKilledInLineOfDuty: value },
		});
	};
	const setDonatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse = (
		value: boolean | undefined
	) => {
		send({
			type: "update_context",
			context: {
				donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse: value,
			},
		});
	};
	const setOwnedThroughCooperativeHousing = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { ownedThroughCooperativeHousing: value },
		});
	};
	const setOwnedThroughNonProfit = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { ownedThroughNonProfit: value },
		});
	};
	const setCollectRentAtThisProperty = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { collectRentAtThisProperty: value },
		});
	};
	const setNoneOfTheAbove = (value: boolean | undefined) => {
		send({ type: "update_context", context: { noneOfTheAbove: value } });
	};
	const setSqFtIncomeProducing = (value: number | undefined) => {
		send({ type: "update_context", context: { sqFtIncomeProducing: value } });
	};
	const setOwnerSsnTin = (value: string | undefined) => {
		send({ type: "update_context", context: { owner1SsnTin: value } });
	};
	const setOwnerHasEnteredSsnTin = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { owner1HasEnteredSsnTin: value },
		});
	};
	const setOwner2SsnTin = (value: string | undefined) => {
		send({ type: "update_context", context: { owner2SsnTin: value } });
	};
	const setOwner2HasEnteredSsnTin = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { owner2HasEnteredSsnTin: value },
		});
	};
	const setTrustDocumentFilesToUpload = (value: FileUploadType[]) => {
		send({
			type: "update_context",
			context: { trustDocumentFilesToUpload: value },
		});
	};
	const setResidencyProofFilesToUpload = React.useCallback(
		(fn: (value: FileUploadType[]) => FileUploadType[]) => {
			send({
				type: "update_context",
				context: {
					residencyProofFilesToUpload: fn(
						state.context?.residencyProofFilesToUpload || []
					),
				},
			});
		},
		[state.context]
	);
	const setOwnerDlFilesToUpload = (value: FileUploadType[]) => {
		send({ type: "update_context", context: { ownerDlFilesToUpload: value } });
	};
	const setOwnerDlVerificationErrored = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { ownerDlVerificationErrored: value },
		});
	};
	const setOwnerHasUploadedDlFile = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { ownerHasUploadedDlFile: value },
		});
	};
	const setOwner2DlFilesToUpload = (value: FileUploadType[]) => {
		send({ type: "update_context", context: { owner2DlFilesToUpload: value } });
	};
	const setOwner2DlVerificationErrored = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { owner2DlVerificationErrored: value },
		});
	};
	const setOwner2HasUploadedDlFile = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { owner2HasUploadedDlFile: value },
		});
	};
	const setFutureSavingsAmount = (value: number | undefined) => {
		send({ type: "update_context", context: { futureSavingsAmount: value } });
	};
	const setRetroSavingsAmount = (value: number | undefined) => {
		send({ type: "update_context", context: { retroSavingsAmount: value } });
	};
	const setManufacturedHomeMake = (value: string | undefined) => {
		send({ type: "update_context", context: { manufacturedHomeMake: value } });
	};
	const setManufacturedHomeModel = (value: string | undefined) => {
		send({ type: "update_context", context: { manufacturedHomeModel: value } });
	};
	const setManufacturedHomeId = (value: string | undefined) => {
		send({ type: "update_context", context: { manufacturedHomeId: value } });
	};
	const setTransferMoveOutDate = (value: string | undefined) => {
		send({ type: "update_context", context: { transferMoveOutDate: value } });
	};
	const setTransferSaleDate = (value: string | undefined) => {
		send({ type: "update_context", context: { transferSaleDate: value } });
	};
	const setTransferOwnerDidNotSellProperty = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { transferOwnerDidNotSellProperty: value },
		});
	};

	const setOwner1DlNumberValue = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { owner1DlNumberValue: value },
		});
	};
	const setOwner2DlNumberValue = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { owner2DlNumberValue: value },
		});
	};
	const setOwner1HasEnteredDlNumber = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { owner1HasEnteredDlNumber: value },
		});
	};
	const setOwner2HasEnteredDlNumber = (value: boolean | undefined) => {
		send({
			type: "update_context",
			context: { owner2HasEnteredDlNumber: value },
		});
	};
	const setNyGovIdAccountUsername = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { nyGovIdAccountUsername: value },
		});
	};
	const setNyGovIdAccountPassword = (value: string | undefined) => {
		send({
			type: "update_context",
			context: { nyGovIdAccountPassword: value },
		});
	};
	const setSpecialFieldsValue = React.useCallback(
		(
			value:
				| Partial<ExemptionQuestions.Answers["specialFieldsValue"]>
				| undefined
		) => {
			send({
				type: "update_context",
				context: {
					specialFieldsValue: {
						...state.context?.specialFieldsValue,
						...value,
					},
				},
			});
		},
		[state.context]
	);

	// response values update react state
	useEffect(() => {
		if (!details) return;

		if (details?.use_info !== null) {
			send({
				type: "update_context",
				context: { use_info: details?.use_info },
			});
		}

		if (details?.state !== null) {
			send({
				type: "update_context",
				context: { state: details?.state },
			});
		}

		if (details?.county !== null) {
			send({
				type: "update_context",
				context: { county: details?.county },
			});
		}

		if (details?.city !== null) {
			send({
				type: "update_context",
				context: { city: details?.city },
			});
		}

		if (details?.exemption_requires_both_licenses !== null) {
			send({
				type: "update_context",
				context: {
					exemption_requires_both_licenses:
						details?.exemption_requires_both_licenses,
				},
			});
		}

		if (details?.auth_company_type !== null) {
			send({
				type: "update_context",
				context: {
					auth_company_type: details?.auth_company_type,
				},
			});
		}

		if (!details?.exemption) return;
		const { exemption } = details;

		if (exemption.is_primary_residence !== null) {
			setPrimaryResidenceValue(!!exemption.is_primary_residence);
		}
		if (exemption.current_homestead_status !== null) {
			setCurrentlyReceivingValue(
				exemption.current_homestead_status as ExemptionCurrentlyReceivingValues
			);
		}
		if (exemption.should_transfer_status !== null) {
			setTransferringHomesteadValue(!!exemption.should_transfer_status);
		}
		if (exemption.purchase_date !== null) {
			setPurchaseDateValue(
				moment(exemption.purchase_date).format(WEBSITE_DATE_FORMAT)
			);
		}
		if (exemption.move_in_date !== null) {
			setMoveInDateValue(
				moment(exemption.move_in_date).format(WEBSITE_DATE_FORMAT)
			);
		}
		if (exemption.owner1_percent_ownership !== null) {
			setOwnerOneValue(exemption.owner1_percent_ownership);
		}
		if (exemption.owner2_percent_ownership !== null) {
			setOwnerTwoValue(exemption.owner2_percent_ownership);
		}
		if (exemption.birth_date !== null) {
			setDobValue(moment(exemption.birth_date).format(WEBSITE_DATE_FORMAT));
		}
		if (exemption.previous_address !== null) {
			setPreviousAddressValue(exemption.previous_address);
		}
		if (exemption.previous_address2 !== null) {
			setPreviousAddress2Value(exemption.previous_address2);
		}
		if (exemption.previous_city !== null) {
			setPreviousCityValue(exemption.previous_city);
		}
		if (exemption.previous_state !== null) {
			setPreviousStateValue(exemption.previous_state);
		}
		if (exemption.previous_zip_code !== null) {
			setPreviousZipcodeValue(exemption.previous_zip_code);
		}
		if (exemption.previous_county !== null) {
			setPreviousZipcodeValue(exemption.previous_county);
		}
		if (exemption.previous_move_out_date !== null) {
			setPreviousMoveOutDateValue(exemption.previous_move_out_date);
		}
		if (exemption.owner2_first_name !== null) {
			setAdditionalOwnerFirstNameValue(exemption.owner2_first_name);
		}
		if (exemption.owner2_last_name !== null) {
			setAdditionalOwnerLastNameValue(exemption.owner2_last_name);
		}
		if (exemption.owner2_email !== null) {
			setAdditionalOwnerEmailValue(exemption.owner2_email);
		}
		if (exemption.owner2_phone !== null) {
			setAdditionalOwnerPhoneValue(exemption.owner2_phone);
		}
		if (exemption.owner2_birth_date !== null) {
			setAdditionalOwnerDobValue(
				moment(exemption.owner2_birth_date).format(WEBSITE_DATE_FORMAT)
			);
		}
		if (exemption.other_property_counties !== null) {
			setOtherCountiesValue(exemption.other_property_counties);
		}
		if (exemption.ownership_status !== null) {
			setOwnersValue(exemption.ownership_status);
		}
		if (exemption.marital_status !== null) {
			setMaritalStatusValue(exemption.marital_status);
		}
		if (exemption.solar_or_wind_has_current_exemptions !== null) {
			setCurrentSolarWindExemptionsValue(
				!!exemption.solar_or_wind_has_current_exemptions
			);
		}
		if (exemption.solar_or_wind_interested_in_installing !== null) {
			setInstallingSolarValue(
				!!exemption.solar_or_wind_interested_in_installing
			);
		}
		if (exemption.solar_or_wind_installed !== null) {
			setSolarWindInstalled(!!exemption.solar_or_wind_installed);
		}
		if (exemption.other_heir_owners_occupy !== null) {
			setOtherHeirOwnersOccupy(!!exemption.other_heir_owners_occupy);
		}
		if (exemption.is_inherited_property !== null) {
			setIsInheritedProperty(!!exemption.is_inherited_property);
		}
		if (exemption.has_disability !== null) {
			setHasDisability(!!exemption.has_disability);
		}
		if (exemption.is_veteran !== null) {
			setIsVeteran(!!exemption.is_veteran);
		}
		if (exemption.surviving_spouse_of_65_plus_person !== null) {
			setSurvivingSpouseOf65PlusPerson(
				!!exemption.surviving_spouse_of_65_plus_person
			);
		}
		if (exemption.disabled_veteran_or_surviving_spouse !== null) {
			setDisabledVeteranOrSurvivingSpouse(
				!!exemption.disabled_veteran_or_surviving_spouse
			);
		}
		if (exemption.surviving_spouse_of_veteran_killed_in_line_of_duty !== null) {
			setSurvivingSpouseOfVeteranKilledInLineOfDuty(
				!!exemption.surviving_spouse_of_veteran_killed_in_line_of_duty
			);
		}
		if (
			exemption.surviving_spouse_of_first_responder_killed_in_line_of_duty !==
			null
		) {
			setSurvivingSpouseOfFirstResponderKilledInLineOfDuty(
				!!exemption.surviving_spouse_of_first_responder_killed_in_line_of_duty
			);
		}
		if (
			exemption.donated_residence_of_partially_disabled_vet_or_surviving_spouse !==
			null
		) {
			setDonatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse(
				!!exemption.donated_residence_of_partially_disabled_vet_or_surviving_spouse
			);
		}
		if (exemption.owned_through_cooporative_housing !== null) {
			setOwnedThroughCooperativeHousing(
				!!exemption.owned_through_cooporative_housing
			);
		}
		if (exemption.owned_through_non_profit !== null) {
			setOwnedThroughNonProfit(!!exemption.owned_through_non_profit);
		}
		if (exemption.collect_rent_at_this_property !== null) {
			setCollectRentAtThisProperty(!!exemption.collect_rent_at_this_property);
		}
		if (exemption.rare_situations_none_of_the_above !== null) {
			setNoneOfTheAbove(!!exemption.rare_situations_none_of_the_above);
		}
		if (exemption.percent_income_producing !== null) {
			setSqFtIncomeProducing(exemption.percent_income_producing);
		}
		if (exemption.owner_driver_license_exempt !== null) {
			setOwnerDlExemptValue(!!exemption.owner_driver_license_exempt);
		}
		if (exemption.owner2_driver_license_exempt !== null) {
			setOwner2DlExemptValue(!!exemption.owner2_driver_license_exempt);
		}
		if (exemption.retro_homestead_savings_estimate !== null) {
			setRetroSavingsAmount(exemption.retro_homestead_savings_estimate);
		}
		if (exemption.manufactured_home_make !== null) {
			setManufacturedHomeMake(exemption.manufactured_home_make);
		}
		if (exemption.manufactured_home_model !== null) {
			setManufacturedHomeModel(exemption.manufactured_home_model);
		}
		if (exemption.manufactured_home_id !== null) {
			setManufacturedHomeId(exemption.manufactured_home_id);
		}
		if (exemption.transfer_move_out_date !== null) {
			setTransferMoveOutDate(
				moment(exemption.transfer_move_out_date).format(WEBSITE_DATE_FORMAT)
			);
		}
		if (exemption.transfer_sale_date !== null) {
			setTransferSaleDate(
				moment(exemption.transfer_sale_date).format(WEBSITE_DATE_FORMAT)
			);
		}
		if (exemption.transfer_owner_did_not_sell_property !== null) {
			setTransferOwnerDidNotSellProperty(
				!!exemption.transfer_owner_did_not_sell_property
			);
		}
		if (exemption.owner1_license_ref !== null) {
			setOwnerHasUploadedDlFile(!!exemption.owner1_license_ref);
		}
		if (exemption.owner2_license_ref !== null) {
			setOwner2HasUploadedDlFile(!!exemption.owner2_license_ref);
		}

		if (exemption.owner1_pii_ref !== null) {
			setOwnerHasEnteredSsnTin(!!exemption.owner1_pii_ref);
		}
		if (exemption.owner2_pii_ref !== null) {
			setOwner2HasEnteredSsnTin(!!exemption.owner2_pii_ref);
		}

		if (exemption.owner1_license_number_ref !== null) {
			setOwner1HasEnteredDlNumber(!!exemption.owner1_license_number_ref);
		}

		if (exemption.owner2_license_number_ref !== null) {
			setOwner2HasEnteredDlNumber(!!exemption.owner2_license_number_ref);
		}

		// Set all special fields at once.
		if (
			exemption.special_fields !== null ||
			details?.auth_company_type !== null
		) {
			setSpecialFieldsValue({
				...exemption.special_fields,
				owned_by_trust: details?.auth_company_type === "trust",
			});
		}
	}, [details]);

	// restart state machine once property details have been loaded
	useEffect(() => {
		if (!loading && id) {
			send({ type: "restart" });
		}
		if (searchParams.get("exemptionsnar") === "ny-tax") {
			send({ type: "ny_tax_nar" });
		}
		if (searchParams.get("exemptionsnar") === "ny-invalid-credentials") {
			send({ type: "ny_invalid_credentials_nar" });
		}
	}, [searchParams, loading, id]);

	const resolvePIIRefType = (prefix: string) => {
		if (flowId === "CALIFORNIA_EXEMPTION")
			return `${prefix}_ssn_tin` as ExemptionPIIRefType;
		if (flowId === "GEORGIA_EXEMPTION" || flowId === "NEW_YORK_EXEMPTION")
			return `${prefix}_ssn` as ExemptionPIIRefType;
		if (flowId === "FLORIDA_EXEMPTION")
			return `${prefix}_ssn` as ExemptionPIIRefType;
		if (flowId === "MICHIGAN_EXEMPTION")
			return `${prefix}_ssn_last_4` as ExemptionPIIRefType;
		return null;
	};

	const submitExemptionFlowAnswers = React.useCallback(async () => {
		if (!details) return;
		await client.submitExemptionsFlowAnswers({
			propertyId: details?.id,
			state: details?.state,
			county: details?.county,
			city: details?.city,
			current_market_value: currentMarketValue,
			is_primary_residence: state.context.primaryResidenceValue ?? null,
			current_homestead_status: state.context.currentlyReceivingValue ?? null,
			should_transfer_status: state.context.transferringHomesteadValue ?? null,
			purchase_date: state.context.purchaseDateValue
				? moment(state.context.purchaseDateValue).format(MYSQL_DATE_FORMAT)
				: null,
			move_in_date: state.context.moveInDateValue
				? moment(state.context.moveInDateValue).format(MYSQL_DATE_FORMAT)
				: null,
			owner1_percent_ownership: state.context.ownerOneValue ?? null,
			owner2_percent_ownership: state.context.ownerTwoValue ?? null,
			birth_date: state.context.dobValue
				? moment(state.context.dobValue).format(MYSQL_DATE_FORMAT)
				: null,
			previous_address: state.context.previousAddressValue ?? null,
			previous_address2: state.context.previousAddress2Value ?? null,
			previous_city: state.context.previousCityValue ?? null,
			previous_state: state.context.previousStateValue ?? null,
			previous_zip_code: state.context.previousZipcodeValue ?? null,
			previous_county: state.context.previousCountyValue ?? null,
			previous_move_out_date: state.context.previousMoveOutDateValue
				? moment(state.context.previousMoveOutDateValue).format(
						MYSQL_DATE_FORMAT
					)
				: null,
			owner2_first_name: state.context.additionalOwnerFirstNameValue ?? null,
			owner2_last_name: state.context.additionalOwnerLastNameValue ?? null,
			owner2_email: state.context.additionalOwnerEmailValue ?? null,
			owner2_phone: state.context.additionalOwnerPhoneValue ?? null,
			owner2_birth_date: state.context.additionalOwnerDobValue
				? moment(state.context.additionalOwnerDobValue).format(
						MYSQL_DATE_FORMAT
					)
				: null,
			other_property_counties: state.context.otherCountiesValue ?? null,
			ownership_status: state.context.ownersValue ?? null,
			marital_status: state.context.maritalStatusValue ?? null,
			solar_or_wind_has_current_exemptions:
				state.context.currentSolarWindExemptionsValue ?? null,
			solar_or_wind_interested_in_installing:
				state.context.installingSolarValue ?? null,
			owner_driver_license_exempt: state.context.ownerDlExemptValue ?? null,
			owner2_driver_license_exempt: state.context.owner2DlExemptValue ?? null,
			solar_or_wind_installed: state.context.solarWindInstalled ?? null,
			other_heir_owners_occupy: state.context.otherHeirOwnersOccupy ?? null,
			is_inherited_property: state.context.isInheritedProperty ?? null,
			has_disability: state.context.hasDisability ?? null,
			surviving_spouse_of_65_plus_person:
				state.context.survivingSpouseOf65PlusPerson ?? null,
			disabled_veteran_or_surviving_spouse:
				state.context.disabledVeteranOrSurvivingSpouse ?? null,
			surviving_spouse_of_veteran_killed_in_line_of_duty:
				state.context.survivingSpouseOfVeteranKilledInLineOfDuty ?? null,
			surviving_spouse_of_first_responder_killed_in_line_of_duty:
				state.context.survivingSpouseOfFirstResponderKilledInLineOfDuty ?? null,
			donated_residence_of_partially_disabled_vet_or_surviving_spouse:
				state.context.donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse ??
				null,
			owned_through_cooporative_housing:
				state.context.ownedThroughCooperativeHousing ?? null,
			owned_through_non_profit: state.context.ownedThroughNonProfit ?? null,
			is_veteran: state.context.isVeteran ?? null,
			collect_rent_at_this_property:
				state.context.collectRentAtThisProperty ?? null,
			rare_situations_none_of_the_above: state.context.noneOfTheAbove ?? null,
			percent_income_producing: state.context.sqFtIncomeProducing ?? null,
			retro_homestead_savings_estimate:
				state.context.retroSavingsAmount ?? null,
			manufactured_home_make: state.context.manufacturedHomeMake ?? null,
			manufactured_home_model: state.context.manufacturedHomeModel ?? null,
			manufactured_home_id: state.context.manufacturedHomeId ?? null,
			transfer_move_out_date: state.context.transferMoveOutDate
				? moment(state.context.transferMoveOutDate).format(MYSQL_DATE_FORMAT)
				: null,
			transfer_sale_date: state.context.transferSaleDate
				? moment(state.context.transferSaleDate).format(MYSQL_DATE_FORMAT)
				: null,
			transfer_owner_did_not_sell_property:
				state.context.transferOwnerDidNotSellProperty ?? null,
			owner1_pii_value: state.context.owner1SsnTin ?? null,
			owner1_pii_ref_type: resolvePIIRefType("owner1"),
			owner2_pii_value: state.context.owner2SsnTin ?? null,
			owner2_pii_ref_type: resolvePIIRefType("owner2"),
			owner1_license_number_value: state.context.owner1DlNumberValue ?? null,
			owner2_license_number_value: state.context.owner2DlNumberValue ?? null,
			ny_gov_id_account_username: state.context.nyGovIdAccountUsername ?? null,
			ny_gov_id_account_password: state.context.nyGovIdAccountPassword ?? null,
			special_fields: state.context.specialFieldsValue ?? null,
		});
		setOwnerHasEnteredSsnTin(!!state.context.owner1SsnTin);
		setOwnerSsnTin(undefined);
		setOwner2HasEnteredSsnTin(!!state.context.owner2SsnTin);
		setOwner2SsnTin(undefined);

		setOwner1HasEnteredDlNumber(!!state.context.owner1DlNumberValue);
		setOwner1DlNumberValue(undefined);
		setOwner2HasEnteredDlNumber(!!state.context.owner2DlNumberValue);
		setOwner2DlNumberValue(undefined);

		setNyGovIdAccountUsername(undefined);
		setNyGovIdAccountPassword(undefined);

		if (
			!!state.context.nyGovIdAccountUsername &&
			!!state.context.nyGovIdAccountPassword
		) {
			setSpecialFieldsValue({
				...state.context.specialFieldsValue,
				ny_gov_id_account_username_provided: true,
				ny_gov_id_account_password_provided: true,
			});
		}
	}, [details?.id, flowId, state]);

	const commonTrackingData = {
		"Property ID": details?.id,
		"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
			? "Yes"
			: "No",
		Address: details?.address,
		State: details?.state,
	};

	const activeQuestionState = React.useMemo(() => {
		if (typeof state.value === "string") {
			return state.value;
		}
		if (typeof state.value === "object" && "NY_TAX_NAR" in state.value) {
			return state.value.NY_TAX_NAR;
		}
		if (
			typeof state.value === "object" &&
			"NY_INVALID_CREDENTIALS_NAR" in state.value
		) {
			return state.value.NY_INVALID_CREDENTIALS_NAR;
		}
		return "";
	}, [state]);

	const activeQuestionId = React.useMemo(() => {
		if (valueIsQuestionId(activeQuestionState)) {
			return activeQuestionState;
		}
		return "";
	}, [activeQuestionState]);

	const next = async (args: TrackEventData = {}) => {
		try {
			let { trackingEventData, skipStep } = args;
			trackingEventData = { ...trackingEventData, ...commonTrackingData };
			await submitExemptionFlowAnswers();
			send({ type: "next" });
			trackAnswerEvent({
				stepName: activeQuestionId,
				data: state.context,
				customData: trackingEventData,
				skipStep: skipStep,
			});
		} catch (e: any) {
			console.error(e);
			sendErrorToWebhook("Error submitting exemptions flow answers", e);
		}
	};

	const prev = async () => {
		try {
			trackBackEvent({
				stepName: activeQuestionId,
				customData: commonTrackingData,
			});
			send({ type: "back" });
			await submitExemptionFlowAnswers();
		} catch (e: any) {
			console.error(e);
			sendErrorToWebhook("Error submitting exemptions flow answers", e);
		}
	};

	const isEligibleForRetro = useMemo(() => {
		return (
			!!state.context.retroSavingsAmount && state.context.retroSavingsAmount > 0
		);
	}, [state.context.retroSavingsAmount]);

	const showDriversLicenseNARs = useMemo(() => {
		if (!details) return false;
		const exemption = details?.exemption;
		if (!exemption) return false;
		const needs_attention_reason = exemption?.needs_attention_reason;
		return exemptionHasNARSubset(needs_attention_reason, EXEMPTION_DL_NARS);
	}, [details]);

	const showFileRelatedNARs = useMemo(() => {
		if (!details) return false;
		const exemption = details?.exemption;
		if (!exemption) return false;
		const needs_attention_reason = exemption?.needs_attention_reason;
		return exemptionHasNARSubset(
			needs_attention_reason,
			EXEMPTION_FILE_RELATED_NARS
		);
	}, [details]);

	const showNYTaxNars = useMemo(() => {
		if (!details) return false;
		const exemption = details?.exemption;
		if (!exemption) return false;
		const needs_attention_reason = exemption?.needs_attention_reason;
		return exemptionHasNARSubset(needs_attention_reason, EXEMPTION_NY_TAX_NARS);
	}, [details]);

	const showNYCredentialsNars = useMemo(() => {
		if (!details) return false;
		const exemption = details?.exemption;
		if (!exemption) return false;
		const needs_attention_reason = exemption?.needs_attention_reason;
		return exemptionHasNARSubset(
			needs_attention_reason,
			EXEMPTION_NY_INVALID_CREDENTIALS_NARS
		);
	}, [details]);

	const Question = React.useMemo(() => {
		if (valueIsQuestionId(activeQuestionState)) {
			return getQuestionComponent(activeQuestionState);
		}
		// eslint-disable-next-line react/display-name
		return () => <React.Fragment></React.Fragment>;
	}, [activeQuestionState]);

	useEffect(() => {
		if (modalToShow === "exemptions") {
			let customTrackEventData = getStepTrackingData(activeQuestionId, details);
			customTrackEventData = { ...customTrackEventData, ...commonTrackingData };
			trackViewPageEvent({
				stepName: activeQuestionId,
				customData: customTrackEventData,
			});
		}
	}, [modalToShow, activeQuestionId, details]);

	useEffect(() => {
		if (modalToShow === "exemptions") {
			trackExemptionQuestionView(activeQuestionId, trackEvent, details);
		}
	}, [modalToShow, activeQuestionId, details, trackEvent]);

	useEffect(() => {
		if (state.context.ownersValue === "Own With Spouse") {
			setOwnerOneValue(100);
			setOwnerTwoValue(100);
		} else {
			setOwnerOneValue(undefined);
			setOwnerTwoValue(undefined);
		}
	}, [state.context.ownersValue]);

	const showBackButton = useMemo(() => state.can({ type: "back" }), [state]);

	return (
		<ExemptionsContext.Provider
			value={{
				flowId,
				activeQuestionId,
				showBackButton,
				prev,
				next,
				Question,
				...state.context,
				status,
				archiveReason,
				setPrimaryResidenceValue,
				setCurrentlyReceivingValue,
				setTransferringHomesteadValue,
				setPurchaseDateValue,
				setMoveInDateValue,
				setOwnerOneValue,
				setOwnerTwoValue,
				setDobValue,
				setPreviousAddressValue,
				setPreviousAddress2Value,
				setPreviousCityValue,
				setPreviousStateValue,
				setPreviousZipcodeValue,
				setPreviousCountyValue,
				setPreviousMoveOutDateValue,
				setAdditionalOwnerFirstNameValue,
				setAdditionalOwnerLastNameValue,
				setAdditionalOwnerEmailValue,
				setAdditionalOwnerPhoneValue,
				setAdditionalOwnerDobValue,
				setOtherCountiesValue,
				setOwnersValue,
				setMaritalStatusValue,
				setCurrentSolarWindExemptionsValue,
				setInstallingSolarValue,
				setOwnerSsnTin,
				setOwnerHasEnteredSsnTin,
				setOwner2SsnTin,
				setOwner2HasEnteredSsnTin,
				setTrustDocumentFilesToUpload,
				setResidencyProofFilesToUpload,
				setOwnerDlFilesToUpload,
				setOwnerDlVerificationErrored,
				setOwnerHasUploadedDlFile,
				setOwnerDlExemptValue,
				setOwner2DlFilesToUpload,
				setOwner2DlVerificationErrored,
				setOwner2HasUploadedDlFile,
				setOwner2DlExemptValue,
				setSolarWindInstalled,
				setOtherHeirOwnersOccupy,
				setIsInheritedProperty,
				setIsVeteran,
				setHasDisability,
				setSurvivingSpouseOf65PlusPerson,
				setDisabledVeteranOrSurvivingSpouse,
				setSurvivingSpouseOfVeteranKilledInLineOfDuty,
				setSurvivingSpouseOfFirstResponderKilledInLineOfDuty,
				setDonatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse,
				setOwnedThroughCooperativeHousing,
				setOwnedThroughNonProfit,
				setCollectRentAtThisProperty,
				setNoneOfTheAbove,
				setSqFtIncomeProducing,
				isEligibleForRetro,
				setFutureSavingsAmount,
				setRetroSavingsAmount,
				setManufacturedHomeMake,
				setManufacturedHomeModel,
				setManufacturedHomeId,
				setTransferMoveOutDate,
				setTransferSaleDate,
				setTransferOwnerDidNotSellProperty,
				setOwner1DlNumberValue,
				setOwner2DlNumberValue,
				setOwner1HasEnteredDlNumber,
				setOwner2HasEnteredDlNumber,
				setSpecialFieldsValue,
				showDriversLicenseNARs,
				showFileRelatedNARs,
				showNYTaxNars,
				showNYCredentialsNars,
				exemptionId,
				setExemptionId,
				setNyGovIdAccountUsername,
				setNyGovIdAccountPassword,
				RestartExemptionApplicationButton,
				exemptionRestarted,
				restartingApplication,
				restartApplication,
				setRestartExemption,
			}}>
			{children}
		</ExemptionsContext.Provider>
	);
};

const trackExemptionQuestionView = (
	activeQuestionId: string,
	trackEvent: (args: any) => void,
	details: PropertyDetails | undefined
) => {
	switch (activeQuestionId) {
		case "INTRO":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Intro_Page_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "PRIMARY_RESIDENCE":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Primary_Residence_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "CURRENTLY_RECEIVING":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Current_Homestead_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "PREVIOUS_RESIDENCE":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Previous_Residence_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "MOVE_IN_DATE":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_MoveInDate_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "DRIVERS_LICENSE_NUMBER":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_DRIVERS_LICENSE_NUMBER_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "DRIVERS_LICENSE_NUMBER2":
			trackEvent({
				eventName:
					constants.EVENTS.Exemptions_Flow_DRIVERS_LICENSE_NUMBER2_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "DRIVERS_LICENSE_NUMBER2":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_VEHICLE_REGISTRATIONS_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "SSN_TIN":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_SSN_TIN_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "SSN_TIN2":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_SSN_TIN2_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "PURCHASE_DATE":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_PurchaseDate_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "DOB":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_DOB_Viewed,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "RARE_SITUATIONS":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Other_Situations_Viewed,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "SQFT":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Income_Producing_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "MARITAL_STATUS":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Marital_Status_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "OWNERS":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Ownership_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "ADDITIONAL_OWNER":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Additional_Owner_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "OWNERSHIP_PERCENT":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Ownership_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
		case "DL_UPLOAD":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Drivers_License_1_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "DL2_UPLAOD":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Drivers_License_2_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "SSN_TIN":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_SSN_TIN_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "SSN_TIN2":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_SSN_TIN2_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "MANUFACTURED_HOME":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Manufactured_Home_Page_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "OTHER_COUNTIES":
			trackEvent({
				eventName:
					constants.EVENTS.Exemptions_Flow_Other_Property_Counties_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "SOLAR_AND_WIND_INSTALLED":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Have_Solar_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "CURRENT_SOLAR_AND_WIND_EXEMPTIONS":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Have_Solar_Exemptions_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "INSTALLING_SOLAR":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Solar_Interest_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "AGREEMENT":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Legal_Page_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "NON_AGREEMENT_CONFIRMATION":
			trackEvent({
				eventName:
					constants.EVENTS.Exemptions_Flow_NonAgreement_Confirmation_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
				},
			});
			break;
		case "MAIN_CONFIRMATION":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Agreement_Confirmation_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
					Address: details?.address,
					"Refund Amount": details?.exemption?.retro_homestead_savings_estimate,
				},
			});
			break;
		case "ANNUAL_HOUSEHOLD_INCOME":
			trackEvent({
				eventName:
					constants.EVENTS.Exemptions_Flow_Annual_Household_Income_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
			break;
		case "TAX_FILING_HISTORY":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Tax_Filing_History_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
			break;
		case "TAX_FILING_HISTORY_2":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Tax_Filing_History_2_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
			break;
		case "TAX_FILING_HISTORY_DETAILS":
			trackEvent({
				eventName:
					constants.EVENTS.Exemptions_Flow_Tax_Filing_History_Details_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
			break;
		case "TAX_FILING_HISTORY_DETAILS_2":
			trackEvent({
				eventName:
					constants.EVENTS.Exemptions_Flow_Tax_Filing_History_Details_2_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
			break;
		case "TRANSFER_STAR_CREDIT":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Transfer_Star_Credit_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
			break;
		case "TRUST_DOCUMENT_UPLOAD":
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Trust_Document_Upload_View,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
			break;
	}
};

const getStepTrackingData = (
	activeQuestionId: string,
	details: PropertyDetails | undefined
): Record<string, any> => {
	switch (activeQuestionId) {
		case "MAIN_CONFIRMATION":
			return {
				"Refund Amount": details?.exemption?.retro_homestead_savings_estimate,
			};
		default:
			return {};
	}
};

const exemptionHasNARSubset = (
	needs_attention_reason: string | null,
	exemptionNarSubset: string[] = []
): boolean => {
	if (!needs_attention_reason) return false;
	return needs_attention_reason
		.split(",")
		.some(nar => nar && exemptionNarSubset.includes(nar));
};

export const exemptionHasNARs = (needs_attention_reason: string | null) => {
	return (
		exemptionHasNARSubset(needs_attention_reason, EXEMPTION_NY_TAX_NARS) ||
		exemptionHasNARSubset(
			needs_attention_reason,
			EXEMPTION_NY_INVALID_CREDENTIALS_NARS
		) ||
		exemptionHasNARSubset(needs_attention_reason, EXEMPTION_DL_NARS) ||
		exemptionHasNARSubset(needs_attention_reason, EXEMPTION_FILE_RELATED_NARS)
	);
};
